import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface UsageMeter {
  used: number;
  /** null means "unlimited" (Pro / Founding tiers for that kind). */
  limit: number | null;
}

export interface UsageStatus {
  is_pro: boolean;
  saves: UsageMeter;
  ai_message: UsageMeter;
  mcp_call: UsageMeter;
  upgrade_url: string;
}

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Reads /api/billing/usage. Powers the Settings meters + 80% soft banner.
 *
 * `enabled` lets ProtectedRoute / Settings turn off the fetch before auth.
 */
export function useUsage(enabled: boolean = true) {
  const [usage, setUsage] = useState<UsageStatus | null>(null);
  const [loading, setLoading] = useState(enabled);
  const aliveRef = useRef(true);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const token = await getToken();
      if (!token) {
        if (aliveRef.current) {
          setUsage(null);
          setLoading(false);
        }
        return;
      }
      const res = await fetch('/api/billing/usage', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (aliveRef.current) setLoading(false);
        return;
      }
      const data = (await res.json()) as UsageStatus;
      if (aliveRef.current) setUsage(data);
    } catch {
      // Non-blocking — caps still work without local meters.
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    aliveRef.current = true;
    refetch();
    return () => {
      aliveRef.current = false;
    };
  }, [refetch]);

  return { usage, loading, refetch };
}

/** Percent used (0..100), clamped, null when unlimited. */
export function usagePercent(meter: UsageMeter): number | null {
  if (meter.limit == null || meter.limit === 0) return null;
  return Math.min(100, Math.round((meter.used / meter.limit) * 100));
}
